import type { RawLog } from './dataset';

/** Deleted when the owner signs out (analyzer/forget.ts), so parsed logs never outlive the session. */
export const LOG_CACHE_DB = 'tune-analyzer';
const LOGS = 'logs';
const META = 'meta';
/** Bump whenever parsing changes, so older parses are ignored and evicted. */
const PARSER_VERSION = 1;
const MAX_ENTRIES = 6;
const MAX_BYTES = 160 * 1024 * 1024;

type Meta = { key: string; datalogId: string; bytes: number; usedAt: number };

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  dbPromise ??= new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(LOG_CACHE_DB, 1);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      request.result.createObjectStore(LOGS);
      request.result.createObjectStore(META, { keyPath: 'key' });
    };
    request.onsuccess = () => {
      const db = request.result;
      // Let a logout delete the database even while a preview is open.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function result<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export function logCacheKey(datalog: { id: string; size: number; updatedAt: string }): string {
  return `v${PARSER_VERSION}:${datalog.id}:${datalog.size}:${datalog.updatedAt}`;
}

function sizeOf(raw: RawLog): number {
  let bytes = raw.time.byteLength;
  for (const channel of raw.channels) bytes += channel.values.byteLength;
  return bytes;
}

/** The parsed log from an earlier visit, or null (no cache, private mode, quota…). */
export async function readCachedLog(key: string): Promise<RawLog | null> {
  try {
    const db = await openDb();
    if (!db) return null;
    const raw = (await result(db.transaction(LOGS).objectStore(LOGS).get(key))) as RawLog | undefined;
    if (!raw) return null;
    const tx = db.transaction(META, 'readwrite');
    const meta = (await result(tx.objectStore(META).get(key))) as Meta | undefined;
    if (meta) tx.objectStore(META).put({ ...meta, usedAt: Date.now() });
    return raw;
  } catch {
    return null;
  }
}

/** Keep a parsed log for next time; the least recently opened are dropped past the limits. */
export async function writeCachedLog(key: string, datalogId: string, raw: RawLog): Promise<void> {
  try {
    const db = await openDb();
    const bytes = sizeOf(raw);
    if (!db || bytes > MAX_BYTES) return;

    const all = (await result(db.transaction(META).objectStore(META).getAll())) as Meta[];
    // Older versions of this file (re-uploaded, or parsed by an older build) go first.
    const stale = all.filter((meta) => meta.datalogId === datalogId && meta.key !== key);
    const kept = all.filter((meta) => !stale.includes(meta) && meta.key !== key).sort((a, b) => b.usedAt - a.usedAt);
    const evict = [...stale];
    let total = bytes;
    let count = 1;
    for (const meta of kept) {
      if (count + 1 > MAX_ENTRIES || total + meta.bytes > MAX_BYTES) evict.push(meta);
      else {
        total += meta.bytes;
        count += 1;
      }
    }

    const tx = db.transaction([LOGS, META], 'readwrite');
    for (const meta of evict) {
      tx.objectStore(LOGS).delete(meta.key);
      tx.objectStore(META).delete(meta.key);
    }
    tx.objectStore(LOGS).put(raw, key);
    tx.objectStore(META).put({ key, datalogId, bytes, usedAt: Date.now() } satisfies Meta);
    await committed(tx);
  } catch {
    // Quota or private mode: the cache is only an optimisation.
  }
}

/** Drop a log's parses: it was deleted. */
export async function dropCachedLog(datalogId: string): Promise<void> {
  try {
    const db = await openDb();
    if (!db) return;
    const all = (await result(db.transaction(META).objectStore(META).getAll())) as Meta[];
    const gone = all.filter((meta) => meta.datalogId === datalogId);
    if (gone.length === 0) return;
    const tx = db.transaction([LOGS, META], 'readwrite');
    for (const meta of gone) {
      tx.objectStore(LOGS).delete(meta.key);
      tx.objectStore(META).delete(meta.key);
    }
    await committed(tx);
  } catch {
    // Quota or private mode: nothing was kept.
  }
}
